import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { baseUrl, getConfig, fetchLandlordIds } from '../../../../../serverUrl';
import { handleUnauthorized } from '../../auth401.ts';

// Helper: triggers logout and returns RTK error shape if response is 401
function checkAuth(response: Response): { error: { status: number; data: string } } | null {
    if (response.status === 401) {
        handleUnauthorized();
        return { error: { status: 401, data: 'Unauthorized' } };
    }
    return null;
}

// Get authorization config
const getConfigData = () => getConfig();

const getLandlordId = async () => {
    try {
        const landlordId = await fetchLandlordIds();
        return landlordId;
    } catch (error) {
        console.error('Error fetching Landlord ID:', error);
        return null;
    }
};

export const reportSlice = createApi({
    reducerPath: 'ReportApi',
    baseQuery: fetchBaseQuery({ baseUrl }),
    tagTypes: ['Report'],
    endpoints: (builder) => ({
        getPropertyReportMetrics: builder.query<void[], { apartment_ids?: string | null; month?: number | null; year?: number | null }>({
            queryFn: async (args) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    // Build query parameters
                    const params = new URLSearchParams();
                    params.append('landlord_id', landlordId);

                    if (args?.apartment_ids) {
                        params.append('apartment_ids', args.apartment_ids);
                    }
                    if (args?.month) {
                        params.append('month', args.month.toString());
                    }
                    if (args?.year) {
                        params.append('year', args.year.toString());
                    }

                    const response = await fetch(
                        `${baseUrl}/landlord/report-landlord-profit-metrics/?${params.toString()}`,
                        getConfigData()
                    );

                    const authError = checkAuth(response); if (authError) return authError;

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: error instanceof Error ? error.message : "Failed to fetch property data" } };
                }
            },
            providesTags: ['Report'],
        }),

        getAnnualReportMetrics: builder.query<void[], { year?: number | null }>({
            queryFn: async (args) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    // Build query parameters
                    const params = new URLSearchParams();
                    params.append('landlord_id', landlordId);

                    if (args?.year) {
                        params.append('year', args.year.toString());
                    }

                    const response = await fetch(
                        `${baseUrl}/landlord/annual-report-landlord-metrics/?${params.toString()}`,
                        getConfigData()
                    );

                    const authError = checkAuth(response); if (authError) return authError;

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();
                    return { data };
                } catch (error) {
                    return { error: { status: 500, data: error instanceof Error ? error.message : "Failed to fetch property data" } };
                }
            },
            providesTags: ['Report'],
        }),

        getAnnualIncomeReport: builder.query<Blob, { year?: number | null }>({
            queryFn: async (args) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const params = new URLSearchParams();
                    params.append('landlord_id', landlordId);
                    params.append('income_category', 'rent');
                    params.append('download', "true");
                    if (args?.year) {
                        params.append('year', args.year.toString());
                    }

                    const response = await fetch(`${baseUrl}/tenant/generate_annual_income_report/?${params.toString()}`, {
                        ...getConfigData(),
                        headers: {
                            ...getConfigData().headers,
                        }
                    });

                    const authError = checkAuth(response); if (authError) return authError;

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    // First check if response is JSON (error case)
                    const contentType = response.headers.get('content-type');
                    if (contentType?.includes('application/json')) {
                        const errorData = await response.json();
                        throw new Error(errorData.message || 'Server returned error');
                    }

                    // Otherwise assume it's a PDF
                    const blob = await response.blob();
                    return { data: blob };
                } catch (error) {
                    return { error: { status: 500, data: error instanceof Error ? error.message : "Failed to fetch report" } };
                }
            },
            providesTags: ['Report'],
        }),
        getAnnualOccupancyReport: builder.query<Blob, { year?: number | null }>({
            queryFn: async (args) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const params = new URLSearchParams();
                    params.append('landlord_id', landlordId);
                    params.append('download', "true");
                    if (args?.year) {
                        params.append('year', args.year.toString());
                    }

                    const response = await fetch(`${baseUrl}/tenant/generate_yearly_apartment_occupancy/?${params.toString()}`, {
                        ...getConfigData(),
                        headers: {
                            ...getConfigData().headers,
                        }
                    });

                    const authError = checkAuth(response); if (authError) return authError;

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    // First check if response is JSON (error case)
                    const contentType = response.headers.get('content-type');
                    if (contentType?.includes('application/json')) {
                        const errorData = await response.json();
                        throw new Error(errorData.message || 'Server returned error');
                    }

                    // Otherwise assume it's a PDF
                    const blob = await response.blob();
                    return { data: blob };
                } catch (error) {
                    return { error: { status: 500, data: error instanceof Error ? error.message : "Failed to fetch report" } };
                }
            },
            providesTags: ['Report'],
        }),
        getAnnualExpenseReport: builder.query<Blob, { year?: number | null }>({
            queryFn: async (args) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const params = new URLSearchParams();
                    params.append('landlord_id', landlordId);
                    params.append('income_category', 'expense');
                    params.append('download', "true");
                    if (args?.year) {
                        params.append('year', args.year.toString());
                    }

                    const response = await fetch(`${baseUrl}/tenant/generate_annual_income_report/?${params.toString()}`, {
                        ...getConfigData(),
                        headers: {
                            ...getConfigData().headers,
                        }
                    });

                    const authError = checkAuth(response); if (authError) return authError;

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                      // First check if response is JSON (error case)
                    const contentType = response.headers.get('content-type');
                    if (contentType?.includes('application/json')) {
                        const errorData = await response.json();
                        throw new Error(errorData.message || 'Server returned error');
                    }

                    // Otherwise assume it's a PDF
                    const blob = await response.blob();
                    return { data: blob };
                } catch (error) {
                    return { error: { status: 500, data: error instanceof Error ? error.message : "Failed to fetch report" } };
                }
            },
            providesTags: ['Report'],
        }),
        getAnnualRepairsReport: builder.query<Blob, { year?: number | null }>({
            queryFn: async (args) => {
                try {
                    const landlordId = await getLandlordId();
                    if (!landlordId) {
                        return { error: { status: 500, data: "Landlord ID not found" } };
                    }

                    const params = new URLSearchParams();
                    params.append('landlord_id', landlordId);
                    params.append('download', "true");
                    if (args?.year) {
                        params.append('year', args.year.toString());
                    }

                    const response = await fetch(`${baseUrl}/tenant/generate-apartment-repairs-report/?${params.toString()}`, {
                        ...getConfigData(),
                        headers: {
                            ...getConfigData().headers,
                        }
                    });

                    const authError = checkAuth(response); if (authError) return authError;

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                      // First check if response is JSON (error case)
                    const contentType = response.headers.get('content-type');
                    if (contentType?.includes('application/json')) {
                        const errorData = await response.json();
                        throw new Error(errorData.message || 'Server returned error');
                    }

                    // Otherwise assume it's a PDF
                    const blob = await response.blob();

                    return { data: blob };
                } catch (error) {
                    return { error: { status: 500, data: error instanceof Error ? error.message : "Failed to fetch report" } };
                }
            },
            providesTags: ['Report'],
        }),
    }),
});

// Export hooks for usage in components
export const { useGetPropertyReportMetricsQuery, useGetAnnualReportMetricsQuery, useGetAnnualIncomeReportQuery, useGetAnnualOccupancyReportQuery, useGetAnnualExpenseReportQuery, useGetAnnualRepairsReportQuery,
    useLazyGetPropertyReportMetricsQuery, useLazyGetAnnualReportMetricsQuery, useLazyGetAnnualIncomeReportQuery, useLazyGetAnnualOccupancyReportQuery, useLazyGetAnnualExpenseReportQuery, useLazyGetAnnualRepairsReportQuery,
} = reportSlice;

